// Profil-Nachbau F-25 Nachtrag (PF-GC-tag-1; output/cockpit-vergleich-20261019/VERGLEICH.md, Empfehlungen 2 und 3; Benny 19.10.2026: „weiter
// mit ki cockpit“): die Ansicht der Hauptgrafik in der Übersicht beider Cockpits. „Je Tag“ (bei Wochen oder Monaten „Je Woche“, „Je Monat“)
// zeigt Säulen je Abschnitt – Recherchen und Nachfragen gestapelt statt einer Summe – mit den Linien „Schnitt …“ und „davor …“;
// „Zusammengezählt“ die Kurve mit Ziellinie und Vorzeitraum. Ohne Ziel der Firma gilt „Je Tag“, mit Ziel „Zusammengezählt“; die Wahl merkt
// sich der Browser je Person, jeder Zugriff in try/catch, ohne Speicher gilt der Standard. Kein Betrag, keine Person, kein Netz; die Wörter
// stehen in cockpitTexte.ts UEBERSICHT_C.ansicht.
import { useState } from 'react';
import { InfoTipp } from '../../components/Erklaerung';
import { BERICHT, UEBERSICHT_C, ZIEL_REGELN } from './cockpitTexte';
import type { Einheit } from './cockpitApi';
import type { DiagrammAbschnitt, DiagrammLinie, TippInhalt } from './Diagramm';
import { tagKurz, tippKopf, zahl, zahlEineStelle } from './format';
import { kurvenTitel, zusammengezaehlt, type Sichtbar } from './ziel';

export type Ansicht = 'tag' | 'summe';
type Speicher = Pick<Storage, 'getItem' | 'setItem'>;
type Reihe = { recherchen: number; nachfragen: number };
type Abschnitt = Reihe & { von: string; bis: string };

/** Beide oder keine Reihe sichtbar: „Fragen“; sonst „Recherchen“ bzw. „Nachfragen“ (wie ziel.ts). */
function reihenWort(s: Sichtbar): string {
  const W = BERICHT.rhythmus.wort;
  return s.recherchen === s.nachfragen ? W.beide : s.recherchen ? W.recherchen : W.nachfragen;
}

// ---- Standard und gemerkte Wahl

/** Standard nach dem Ziel der Firma: ohne Ziel „Je Tag“, mit Ziel „Zusammengezählt“, weil nur dort die Ziellinie Sinn ergibt. Die Plattform
 *  hat kein Ziel für Recherchen und beginnt mit „Je Tag“. */
export function ansichtStandard(ziel: number | null): Ansicht {
  return ziel === null ? 'tag' : 'summe';
}

/** Schlüssel der Wahl je Cockpit und Person (Kennung des Kontos, ohne Namen). */
export function ansichtSchluessel(cockpit: 'firma' | 'plattform', person: number | string | null | undefined): string {
  return `ki-cockpit-ansicht:${cockpit}:${person ?? 'ohne'}`;
}

/** Die gemerkte Wahl oder null: keine Wahl, ein fremder Wert, ein gesperrter Speicher oder kein Browser. */
export function ansichtLesen(schluessel: string, speicher?: Speicher): Ansicht | null {
  try {
    const wert = (speicher ?? window.localStorage).getItem(schluessel);
    return wert === 'tag' || wert === 'summe' ? wert : null;
  } catch { return null; }
}

/** Merkt die Wahl; wirft der Speicher, gilt sie bis zum Neuladen. */
export function ansichtMerken(schluessel: string, wert: Ansicht, speicher?: Speicher): void {
  try { (speicher ?? window.localStorage).setItem(schluessel, wert); } catch { /* gilt bis zum Neuladen */ }
}

/** Die Wahl dieser Person in diesem Browser (null: keine, dann gilt der Standard der Seite) und das Wählen. */
export function useAnsichtWahl(schluessel: string): [Ansicht | null, (neu: Ansicht) => void] {
  const [wahl, setWahl] = useState<Ansicht | null>(() => ansichtLesen(schluessel));
  const waehlen = (neu: Ansicht) => { setWahl(neu); ansichtMerken(schluessel, neu); };
  return [wahl, waehlen];
}

// ---- Säulen „Je Tag“

/** Je Abschnitt Recherchen und Nachfragen als zwei Werte, die das Diagramm nach den Häkchen stapelt: zwei Reihen statt einer Summe
 *  (Vergleich B13). */
export function tagAbschnitte(reihen: Abschnitt[]): DiagrammAbschnitt[] {
  return reihen.map(r => ({ von: r.von, bis: r.bis, werte: [r.recherchen, r.nachfragen] }));
}

/** Titel der Säulen: „Recherchen je Tag“, mit Nachfragen „Fragen je Tag“; bei Woche und Monat „… je Woche“ bzw. „… je Monat“ (ziel.ts). */
export function tagTitel(s: Sichtbar, einheit: Einheit): string {
  return einheit === 'TAG' ? UEBERSICHT_C.ansicht.titelTag(reihenWort(s)) : kurvenTitel(s, einheit);
}

/** Schnitt je Abschnitt der sichtbaren Reihen, wie die gestapelte Säule: Summe geteilt durch die Zahl der Abschnitte; ohne Abschnitt null. */
export function schnittJeAbschnitt(reihen: Reihe[], s: Sichtbar): number | null {
  if (!reihen.length) return null;
  return reihen.reduce((summe, r) => summe + (s.recherchen ? r.recherchen : 0) + (s.nachfragen ? r.nachfragen : 0), 0) / reihen.length;
}

/** Waagrechte Linien der Säulen: „Schnitt {Zahl}“ rechts, „davor {Zahl}“ (Schnitt des Vorzeitraums) links; „davor“ nicht beim Jahr und
 *  ohne Vorzeitraum, wie bei der Kurve. Ein Schnitt von 0 bekommt keine Linie, sie läge auf der Achse. */
export function schnittLinien(l: { jetzt: Reihe[]; davor: Reihe[] | null; sichtbar: Sichtbar; jahr: boolean }): DiagrammLinie[] {
  const A = UEBERSICHT_C.ansicht;
  const jetzt = schnittJeAbschnitt(l.jetzt, l.sichtbar);
  const davor = l.davor && !l.jahr ? schnittJeAbschnitt(l.davor, l.sichtbar) : null;
  const linien: DiagrammLinie[] = [];
  if (jetzt !== null && jetzt > 0) linien.push({ wert: jetzt, text: A.schnitt(zahlEineStelle(jetzt)), art: 'schnitt' });
  if (davor !== null && davor > 0) linien.push({ wert: davor, text: A.davor(zahlEineStelle(davor)), art: 'vorher', seite: 'links' });
  return linien;
}

// ---- „Zusammengezählt“ bei Woche oder Monat (die Tageskurve von ziel.ts zielKurve braucht Tage)

/** Laufende Summe der sichtbaren Reihen je Abschnitt (ziel.ts zusammengezaehlt), daneben der Vorzeitraum Abschnitt für Abschnitt. */
export function summeAbschnitte(l: { jetzt: Abschnitt[]; davor: Reihe[] | null; sichtbar: Sichtbar }): { abschnitte: DiagrammAbschnitt[]; endwert: number } {
  const jetzt = zusammengezaehlt(l.jetzt, l.sichtbar);
  const davor = l.davor ? zusammengezaehlt(l.davor, l.sichtbar, l.jetzt.length) : null;
  return { abschnitte: l.jetzt.map((r, i) => ({ von: r.von, bis: r.bis, werte: [jetzt[i] ?? null, davor?.[i] ?? null] })),
    endwert: jetzt[jetzt.length - 1] ?? 0 };
}

/** Tooltip eines Abschnitts von summeAbschnitte: Kopf wie bei den Säulen, „bis {Tag}“, „davor bis {Tag}“, „Ziel“ und der Fuß der Tageskurve. */
export function summeTipp(l: { einheit: Einheit; heute: string; von: string; abschnitte: DiagrammAbschnitt[]; davor: { bis: string }[] | null;
  ziel: number | null; sichtbar: Sichtbar }, i: number): TippInhalt {
  const K = ZIEL_REGELN.kurve, a = l.abschnitte[i], jetzt = a.werte[0], davor = a.werte[1], alt = l.davor?.[i];
  return { kopf: tippKopf(l.einheit, a.von, a.bis, l.heute), zeilen: [
    ...(typeof jetzt === 'number' ? [{ farbe: 'recherchen' as const, name: K.bis(tagKurz(a.bis)), wert: zahl(jetzt) }] : []),
    ...(typeof davor === 'number' && alt ? [{ farbe: 'vorzeitraum' as const, name: K.davorBis(tagKurz(alt.bis)), wert: zahl(davor) }] : []),
    ...(l.ziel !== null ? [{ name: K.ziel, wert: zahl(l.ziel) }] : []),
  ], fuss: K.fuss(reihenWort(l.sichtbar), tagKurz(l.von)) };
}

// ---- Umschalter

/** Über der Hauptgrafik: zwei Knöpfe mit aria-pressed in der Gruppe „Ansicht der Grafik“, je 44 px hoch, mit Tab und Enter bzw. Leertaste;
 *  der erste heißt nach der Einheit des Verlaufs. Dahinter das Info-Symbol. */
export function AnsichtUmschalter({ ansicht, einheit, info, onWahl }: { ansicht: Ansicht; einheit: Einheit; info: string;
  onWahl: (neu: Ansicht) => void }) {
  const A = UEBERSICHT_C.ansicht;
  return <div className="ki-ansicht">
    <div className="ki-ansicht-knoepfe" role="group" aria-label={A.gruppe}>
      <button type="button" aria-pressed={ansicht === 'tag'} onClick={() => onWahl('tag')}>{A.je[einheit]}</button>
      <button type="button" aria-pressed={ansicht === 'summe'} onClick={() => onWahl('summe')}>{A.summe}</button>
    </div>
    <InfoTipp wort={A.gruppe} text={info} />
  </div>;
}
